import {
    AppId,
    CountriesSelect, equalInLC,
    FCX,
    Header,
    HeaderLogo,
    ICountriesSelectOption,
    ProductLogo,
} from "@datassential/platform-ui-lib";
import { Link, matchPath, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    countriesSelector,
    currentCountrySelector,
} from "@selectors/countries.selector";
import { useCallback, useEffect, useMemo } from "react";
import { setCurrent } from "@actions/countries.actions";
import { ICountry } from "@models";
import './PlatformHeader.scss';
import { AVAILABLE_COUNTRIES } from "@reducers/countries.reducer";
import HeaderNavigation from "@containers/PlatformHeader/HeaderNavigation";
import { Routes } from "@routes";
import RestaurantsMenu from "@vms/RestaurantMenu";
import {
    AI_SEARCH_QUERY_PARAM,
    BYO_INSTANT_CHART_ID_QUERY_PARAM_NAME,
    BYO_SETTINGS_ID_QUERY_PARAM_NAME
} from "@constants/general";
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";

function getCountryNameByCode(code: string): string {
    switch (code.toLowerCase()) {
        case 'usa':
            return 'United States';
        case 'can':
            return 'Canada';
        default:
            return '';
    }
}

const PlatformHeader: FCX = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const currentCountry = useSelector(currentCountrySelector);
    const countries: ICountry[] = useSelector(countriesSelector);
    const { resetSearch } = useAiSearchSettingsContext();
    

    const setCurrentCountry = useCallback(
        (countryCode: string) => {
            if (!equalInLC(countryCode, currentCountry)) {
                dispatch(setCurrent(countryCode));
                resetSearch();
                window.location.reload();
            }
        },
        [dispatch, currentCountry, resetSearch]
    );

    const countriesOptions = useMemo(
        (): ICountriesSelectOption[] => countries
            .filter(option => AVAILABLE_COUNTRIES.some(
                i => equalInLC(i, option.countryCode)
            ))
            .filter(option => option.hasAccess)
            .map(option => ({
                id: option.countryCode,
                shortName: option.countryCode,
                name: getCountryNameByCode(option.countryCode),
            })),
        [countries]
    );

    const isByoPageForOneCountry = useMemo(
        (): boolean => {
            return !!matchPath(Routes.byoSetting, location.pathname)
                || !!matchPath(Routes.instantChart, location.pathname);
        },
        [location.pathname]
    );

    const isCountriesDropdownDisabled = useMemo(
        (): boolean => (
            countriesOptions.length === 1
            || !!matchPath(Routes.foodProfile, location.pathname)
            || !!matchPath(Routes.foodProfiles, location.pathname)
            || !!matchPath(Routes.instantCharts, location.pathname)
            || isByoPageForOneCountry
        ),
        [location.pathname, countriesOptions.length, isByoPageForOneCountry]
    );

    const isAiSearchActive = location.search.includes(AI_SEARCH_QUERY_PARAM);

    const isPageWithFilters = useMemo(
        () => !!matchPath(Routes.topTrends, location.pathname)
            || !!matchPath(Routes.byo, location.pathname)
            || !!matchPath(Routes.byoSetting, location.pathname)
            || !!matchPath(Routes.instantChart, location.pathname),
        [location.pathname]
    );

    useEffect(() => {
        if (isAiSearchActive) {
            setCurrentCountry('usa');
        }
    }, [isAiSearchActive, setCurrentCountry]);

    return (
        <Header
            className="PlatformHeader"
            leftContent={<>
                <Link to="/" data-testid={"buttonHeaderLogo"}>
                    <HeaderLogo>
                        <ProductLogo
                            className="PlatformHeader__logo is-large"
                            appId={AppId.MenuTrends}
                        />
                        <ProductLogo
                            className="PlatformHeader__logo is-small"
                            appId={AppId.MenuTrends}
                            type="regular"
                        />
                    </HeaderLogo>
                </Link>
                <CountriesSelect
                    style={{ marginLeft: 10 }}
                    options={countriesOptions}
                    value={currentCountry}
                    setValue={(value) => setCurrentCountry(value as string)}
                    testId={"selectHeaderCountry"}
                    isDisabled={isCountriesDropdownDisabled}
                />
                {isPageWithFilters && <RestaurantsMenu/>}
            </>}
            rightContent={<HeaderNavigation/>}
        />
    );
};

export default PlatformHeader;